import { Injectable } from '@angular/core';
import { NgbModal, NgbModalOptions, NgbModalRef } from '@ng-bootstrap/ng-bootstrap';

import { LegacyModalComponent } from './legacy-modal.component';

/**
 * Drop-in replacement for AngularJS $uibModal that routes AJS-style
 * `.open({ controller, templateUrl, resolve })` calls through NgbModal
 * with a generic wrapper component.
 *
 * Angular-style `.open(ComponentClass, opts)` calls pass through unchanged.
 */
@Injectable({ providedIn: 'root' })
export class LegacyUibModalService {
  constructor ( private ngbModal: NgbModal ) {}

  /**
   * Overloaded open:
   *  - open(ComponentClass, options?)       → standard NgbModal path
   *  - open({ controller, templateUrl, … }) → legacy AJS path via wrapper
   */
  open ( contentOrConfig: any, options?: NgbModalOptions ): NgbModalRef {
    // Angular-style: first arg is a class/function (component)
    if (typeof contentOrConfig === 'function') {

      return this.ngbModal.open(contentOrConfig, options);

    }

    // AJS-style: first arg is a config object with controller/templateUrl
    const config = contentOrConfig;

    const ngbOptions: NgbModalOptions = {
      backdrop: config.backdrop ?? 'static',
      keyboard: config.keyboard ?? false };

    if (config.windowClass) {


      ngbOptions.windowClass = config.windowClass;


    }

    if (config.size) {


      ngbOptions.size = config.size;


    }

    const modalRef = this.ngbModal.open(LegacyModalComponent, ngbOptions);

    // Resolve all resolve functions and assign to component
    const resolved: any = {};

    if (config.resolve) {
      for (const key of Object.keys(config.resolve)) {
        const val = config.resolve[key];

        resolved[key] = typeof val === 'function' ? val() : val;
      }
    }

    modalRef.componentInstance.resolve      = resolved;
    modalRef.componentInstance.title        = config.title || '';
    modalRef.componentInstance.templateHtml = typeof config.templateUrl === 'string' ? config.templateUrl : '';

    // If caller provided an inline controller function (array or function),
    // convert it to a scope-setup function
    if (typeof config.controller === 'function') {
      modalRef.componentInstance.legacyController = ( scope: any ) => {
        config.controller(scope, modalRef);
      };
    }
    else if (Array.isArray(config.controller)) {
      // Array annotation: ['dep1', 'dep2', fn]
      const fn = config.controller[config.controller.length - 1];

      if (typeof fn === 'function') {
        modalRef.componentInstance.legacyController = ( scope: any ) => {
          fn(scope, modalRef);
        };
      }
    }

    return modalRef;
  }

  /**
   * Convenience method matching $uibModal.hasOpenModals()
   */
  hasOpenModals (): boolean {

    return this.ngbModal.hasOpenModals();

  }

  /**
   * Convenience method matching $uibModal.dismissAll()
   */
  dismissAll ( reason?: any ): void {

    return this.ngbModal.dismissAll(reason);

    };

  }
